/**
 * LocalStorage 封装工具类
 *
 * 提供对浏览器 localStorage 的封装，支持自动序列化/反序列化对象
 *
 * @example
 * ```typescript
 * // 存储字符串
 * STORE.set('key', 'value')
 *
 * // 存储对象
 * STORE.set('user', { name: 'John', age: 30 })
 *
 * // 获取数据
 * const user = STORE.get('user')
 *
 * // 删除数据
 * STORE.remove('key')
 *
 * // 清空所有数据
 * STORE.clear()
 * ```
 */
class STORE {
  /**
   * localStorage 实例
   * @private
   */
  private static _store = window.localStorage

  /**
   * 存储数据到 localStorage
   *
   * 对象类型会自动序列化为 JSON 字符串，其他类型直接存储
   *
   * @param key - 存储键名
   * @param value - 存储值，支持任意类型
   *
   * @example
   * ```typescript
   * STORE.set('username', 'John')
   * STORE.set('config', { theme: 'dark', lang: 'en' })
   * ```
   */
  public static set<T>(key: string, value: T) {
    try {
      const type = Object.prototype.toString.call(value)
      switch (type) {
        case "[object Object]":
        case "[object Array]":
          this._store.setItem(key, JSON.stringify(value))
          break
        default:
          this._store.setItem(key, String(value))
          break
      }
    } catch {
      // localStorage 可能满或不可用，静默降级
    }
  }

  /**
   * 从 localStorage 获取数据
   *
   * 自动尝试将存储的 JSON 字符串解析为对象
   *
   * @param key - 存储键名
   * @returns 解析后的数据，如果解析失败则抛出错误
   * @throws {Error} 当 JSON 解析失败时抛出 'storage parse error'
   *
   * @example
   * ```typescript
   * const username = STORE.get('username')
   * const config = STORE.get('config')
   * ```
   */
  public static get<T>(key: string): T | null {
    try {
      const value = this._store.getItem(key)
      if (value === null) return null
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }

  /**
   * 清空 localStorage 中的所有数据
   *
   * @example
   * ```typescript
   * STORE.clear()
   * ```
   */
  public static clear() {
    return this._store.clear()
  }

  /**
   * 从 localStorage 中删除指定键的数据
   *
   * @param key - 要删除的存储键名
   *
   * @example
   * ```typescript
   * STORE.remove('username')
   * ```
   */
  public static remove(key: string) {
    return this._store.removeItem(key)
  }
}

export default STORE
